import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { NoteDialog } from '@/components/ui/note-dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useToast } from '@/hooks/use-toast'
import apiClient from '@/lib/apiClient'
import type { Order as ApiOrder } from '@/types/api'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  CheckCircle,
  ChevronDown,
  Clock,
  Eye,
  Loader2,
  Package,
  Plus,
  Trash2,
  Truck,
  XCircle,
} from 'lucide-react'
import React, { useMemo, useState } from 'react'

interface Order {
  id: string
  orderNumber: string
  customerName: string
  customerEmail: string
  customerPhone: string
  status: 'pending' | 'review' | 'processing' | 'shipped' | 'delivered' | 'cancelled'
  paymentStatus: 'pending' | 'paid' | 'failed' | 'refunded'
  total: number
  itemCount: number
  shippingAddress: {
    fullName: string
    phone: string
    city: string
    province: string
    fullAddress: string
  }
  orderDate: string
  estimatedDelivery?: string
  trackingNumber?: string
  notes?: string
}

interface OrderListProps {
  className?: string
  onViewOrder: (_orderId: string) => void
  onAddNote?: (_orderId: string, _note: string) => void
}

const mapApiOrderToComponent = (apiOrder: ApiOrder): Order => {
  const user = apiOrder.user
  const shippingAddressData = apiOrder.shipping_address
  const phone = user?.phone || shippingAddressData?.phone || 'N/A'

  const shippingAddress = shippingAddressData
    ? {
        fullName: shippingAddressData.full_name || 'N/A',
        phone: shippingAddressData.phone || 'N/A',
        city: shippingAddressData.city || 'N/A',
        province: shippingAddressData.state || shippingAddressData.province || 'N/A',
        fullAddress: `${shippingAddressData.address || shippingAddressData.address_line_1 || ''}${
          shippingAddressData.address_line_2 ? `, ${shippingAddressData.address_line_2}` : ''
        }, ${shippingAddressData.city}${shippingAddressData.state && shippingAddressData.state !== shippingAddressData.city ? `, ${shippingAddressData.state}` : ''}${shippingAddressData.province && shippingAddressData.province !== shippingAddressData.city ? `, ${shippingAddressData.province}` : ''}`,
      }
    : {
        fullName: 'N/A',
        phone: 'N/A',
        city: 'N/A',
        province: 'N/A',
        fullAddress: 'N/A',
      }

  return {
    id: apiOrder.id,
    orderNumber: apiOrder.order_number,
    customerName: user?.full_name || 'N/A',
    customerEmail: user?.email || 'N/A',
    customerPhone: phone,
    status: apiOrder.status as Order['status'],
    paymentStatus: apiOrder.payment_status as Order['paymentStatus'],
    total: apiOrder.total_amount,
    itemCount: apiOrder.items?.length || 0,
    shippingAddress,
    orderDate: new Date(apiOrder.created_at).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }),
    estimatedDelivery: undefined,
    trackingNumber: apiOrder.tracking_number,
    notes: apiOrder.notes,
  }
}

export const OrderList: React.FC<OrderListProps> = ({ className, onViewOrder, onAddNote }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [paymentFilter, setPaymentFilter] = useState<string>('all')
  const [selectedOrders, setSelectedOrders] = useState<string[]>([])
  const [noteDialogOrder, setNoteDialogOrder] = useState<string | null>(null)
  const { toast } = useToast()
  const queryClient = useQueryClient()

  // If no onAddNote prop is provided, create a default mutation
  const defaultAddNoteMutation = useMutation({
    mutationFn: async ({ orderId, note }: { orderId: string; note: string }) => {
      return apiClient.updateOrder(orderId, {
        notes: note,
        append_notes: true,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      toast({
        title: 'Success',
        description: 'Note added to order successfully',
      })
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to add note. Please try again.',
        variant: 'destructive',
      })
    },
  })

  const handleAddNote = (orderId: string, note: string) => {
    if (onAddNote) {
      onAddNote(orderId, note)
    } else {
      defaultAddNoteMutation.mutate({ orderId, note })
    }
    setNoteDialogOrder(null)
  }

  const cancelOrderMutation = useMutation({
    mutationFn: async (orderId: string) => {
      return apiClient.cancelOrder(orderId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      toast({
        title: 'Success',
        description: 'Order cancelled successfully',
      })
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to cancel order. Please try again.',
        variant: 'destructive',
      })
    },
  })

  const handleCancelOrder = (orderId: string) => {
    cancelOrderMutation.mutate(orderId)
  }

  const {
    data: ordersResponse,
    isLoading: ordersLoading,
    error: ordersError,
  } = useQuery({
    queryKey: ['orders', { search: searchTerm, status: statusFilter, payment: paymentFilter }],
    queryFn: async () => {
      const params: Record<string, string> = {}
      if (searchTerm) params.search = searchTerm
      if (statusFilter !== 'all') params.status = statusFilter
      if (paymentFilter !== 'all') params.payment_status = paymentFilter
      return apiClient.getOrders({ ...params, per_page: 100 })
    },
  })

  const { data: statsResponse, isLoading: statsLoading } = useQuery({
    queryKey: ['orderStats', 'all'],
    queryFn: async () => {
      return apiClient.getOrderStats({ period: 'all' })
    },
  })

  const orders = useMemo(() => {
    if (!ordersResponse?.data) return []
    return ordersResponse.data.map(mapApiOrderToComponent)
  }, [ordersResponse])

  const filteredOrders = useMemo(() => {
    return orders.filter((order: Order) => {
      const matchesSearch =
        order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.customerEmail.toLowerCase().includes(searchTerm.toLowerCase())

      const matchesStatus = statusFilter === 'all' || order.status === statusFilter
      const matchesPayment = paymentFilter === 'all' || order.paymentStatus === paymentFilter

      return matchesSearch && matchesStatus && matchesPayment
    })
  }, [orders, searchTerm, statusFilter, paymentFilter])

  const stats = statsResponse?.data || {
    total_orders: 0,
    total_pending: 0,
    total_review: 0,
    total_paid_status: 0,
    total_processing: 0,
    total_shipped: 0,
    total_delivered: 0,
    total_cancelled: 0,
    total_refunded: 0,
  }

  const totalOrders = stats.total_orders

  const handleSelectAll = () => {
    if (selectedOrders.length === filteredOrders.length) {
      setSelectedOrders([])
    } else {
      setSelectedOrders(filteredOrders.map((o: Order) => o.id))
    }
  }

  const handleSelectOrder = (orderId: string) => {
    setSelectedOrders((prev) =>
      prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]
    )
  }

  const getStatusVariant = (status: Order['status']) => {
    switch (status) {
      case 'pending':
        return 'secondary'
      case 'processing':
        return 'default'
      case 'shipped':
        return 'outline'
      case 'delivered':
        return 'default'
      case 'cancelled':
        return 'destructive'
      default:
        return 'secondary'
    }
  }

  const getStatusIcon = (status: Order['status']) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4" />
      case 'processing':
        return <Package className="h-4 w-4" />
      case 'shipped':
        return <Truck className="h-4 w-4" />
      case 'delivered':
        return <CheckCircle className="h-4 w-4" />
      case 'cancelled':
        return <XCircle className="h-4 w-4" />
      default:
        return <Clock className="h-4 w-4" />
    }
  }

  const getPaymentStatusVariant = (status: Order['paymentStatus']) => {
    switch (status) {
      case 'paid':
        return 'default'
      case 'pending':
        return 'secondary'
      case 'failed':
        return 'destructive'
      case 'refunded':
        return 'outline'
      default:
        return 'secondary'
    }
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(price)
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>Orders</CardTitle>
        <div className="flex items-center space-x-4">
          <Input
            placeholder="Search orders..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-64"
          />

          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pr-8 text-sm ring-offset-background appearance-none"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="review">Review</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <ChevronDown className="absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 pointer-events-none text-gray-500" />
          </div>

          <div className="relative">
            <select
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pr-8 text-sm ring-offset-background appearance-none"
            >
              <option value="all">All Payment</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>
            <ChevronDown className="absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 pointer-events-none text-gray-500" />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Bulk Actions */}
        {selectedOrders.length > 0 && (
          <div className="mb-4 p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {selectedOrders.length} orders selected
              </span>
              <div className="flex space-x-2">
                <Button variant="outline" size="sm">
                  Export Selected
                </Button>
                <Button variant="outline" size="sm">
                  Bulk Status Update
                </Button>
                <Button variant="outline" size="sm">
                  Send Notifications
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="p-4 border rounded-lg">
            <div className="text-sm text-muted-foreground">Total Orders</div>
            {statsLoading ? (
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            ) : (
              <div className="text-2xl font-bold">{totalOrders}</div>
            )}
          </div>
          <div className="p-4 border rounded-lg">
            <div className="text-sm text-muted-foreground">Pending</div>
            {statsLoading ? (
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            ) : (
              <div className="text-2xl font-bold text-yellow-600">{stats.total_pending}</div>
            )}
          </div>
          <div className="p-4 border rounded-lg">
            <div className="text-sm text-muted-foreground">Processing</div>
            {statsLoading ? (
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            ) : (
              <div className="text-2xl font-bold text-blue-600">{stats.total_processing}</div>
            )}
          </div>
          <div className="p-4 border rounded-lg">
            <div className="text-sm text-muted-foreground">Shipped</div>
            {statsLoading ? (
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            ) : (
              <div className="text-2xl font-bold text-purple-600">{stats.total_shipped}</div>
            )}
          </div>
          <div className="p-4 border rounded-lg">
            <div className="text-sm text-muted-foreground">Delivered</div>
            {statsLoading ? (
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            ) : (
              <div className="text-2xl font-bold text-green-600">{stats.total_delivered}</div>
            )}
          </div>
        </div>

        {/* Loading State */}
        {ordersLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">Loading orders...</span>
          </div>
        )}

        {/* Error State */}
        {ordersError && (
          <div className="text-center py-12">
            <div className="text-destructive mb-4">Failed to load orders. Please try again.</div>
            <Button variant="outline" onClick={() => window.location.reload()}>
              Retry
            </Button>
          </div>
        )}

        {/* Table */}
        {!ordersLoading && !ordersError && (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300"
                      checked={
                        filteredOrders.length > 0 && selectedOrders.length === filteredOrders.length
                      }
                      onChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Order</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Order Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-12">
                      <div className="text-muted-foreground">No orders found</div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredOrders.map((order: Order) => (
                    <TableRow key={order.id}>
                      <TableCell>
                        <input
                          type="checkbox"
                          className="rounded border-gray-300"
                          checked={selectedOrders.includes(order.id)}
                          onChange={() => handleSelectOrder(order.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{order.orderNumber}</div>
                          {order.trackingNumber && (
                            <div className="text-xs text-muted-foreground">
                              Tracking: {order.trackingNumber}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{order.customerName}</div>
                          <div className="text-sm text-muted-foreground">{order.customerEmail}</div>
                          <div className="text-xs text-muted-foreground">{order.customerPhone}</div>
                          <div className="text-xs text-muted-foreground">
                            {order.shippingAddress.fullName !== 'N/A' &&
                              order.shippingAddress.fullName !== order.customerName && (
                                <>Ship to: {order.shippingAddress.fullName}</>
                              )}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {order.shippingAddress.city !== 'N/A' && (
                              <>
                                {order.shippingAddress.city}, {order.shippingAddress.province}
                              </>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={getStatusVariant(order.status)}
                          className="flex items-center gap-1 w-fit"
                        >
                          {getStatusIcon(order.status)}
                          {order.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getPaymentStatusVariant(order.paymentStatus)}>
                          {order.paymentStatus}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatPrice(order.total)}</TableCell>
                      <TableCell>
                        <span className="text-sm">{order.itemCount} items</span>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="text-sm">{order.orderDate}</div>
                          {order.estimatedDelivery && (
                            <div className="text-xs text-muted-foreground">
                              Est. {order.estimatedDelivery}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => onViewOrder(order.id)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setNoteDialogOrder(order.id)}>
                              <Plus className="h-4 w-4 mr-2" />
                              Add Note
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => handleCancelOrder(order.id)}
                              disabled={
                                order.status === 'cancelled' || cancelOrderMutation.isPending
                              }
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Cancel Order
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Empty State */}
        {!ordersLoading && !ordersError && filteredOrders.length === 0 && searchTerm && (
          <div className="text-center py-12">
            <div className="text-muted-foreground">No orders found matching "{searchTerm}"</div>
            <Button variant="outline" className="mt-4" onClick={() => setSearchTerm('')}>
              Clear Search
            </Button>
          </div>
        )}
      </CardContent>

      {/* Note Dialog */}
      {noteDialogOrder && (
        <NoteDialog
          _orderId={noteDialogOrder}
          existingNotes={filteredOrders.find((o: Order) => o.id === noteDialogOrder)?.notes}
          onAddNote={handleAddNote}
          isSubmitting={defaultAddNoteMutation.isPending}
          open={!!noteDialogOrder}
          onOpenChange={(open) => !open && setNoteDialogOrder(null)}
        />
      )}
    </Card>
  )
}
